/** Canvas-scoped keyboard editing; repeat speed is independent of OS key repeat. */
export type PathEditorKeyboardActions = {
  enabled: () => boolean;
  hasSelection: () => boolean;
  move: (dx: number, dy: number) => void;
  remove: () => void;
  clear: () => void;
  begin?: () => void;
  commit?: () => void;
  undo?: () => void;
  redo?: () => void;
};
export type EditorAnimationClock = {
  request: (callback: (time: number) => void) => number;
  cancel: (id: number) => void;
  blurTarget?: EventTarget;
  visibilityTarget?: EventTarget;
  hidden?: () => boolean;
};
const arrows: Record<string, { x: number; y: number }> = {
  ArrowLeft: { x: -1, y: 0 }, ArrowRight: { x: 1, y: 0 },
  ArrowUp: { x: 0, y: -1 }, ArrowDown: { x: 0, y: 1 },
};
const textEntrySelector = 'input, textarea, select, [contenteditable]:not([contenteditable="false"])';
function matchesClosest(target: EventTarget | null, selector: string) {
  const node = target as Element | null;
  return Boolean(node && typeof node.closest === 'function' && node.closest(selector));
}
function historyCommand(event: KeyboardEvent, actions: { undo?: () => void; redo?: () => void }) {
  if (event.altKey || !(event.ctrlKey || event.metaKey)) return undefined;
  const key = event.key.toLowerCase();
  return key === 'z' ? (event.shiftKey ? actions.redo : actions.undo) : key === 'y' ? actions.redo : undefined;
}

/** History follows focus within the editor region, including its tab and
 * toolbar buttons. Form fields keep their native text undo/redo behavior. */
export function bindPathEditorHistory(region: HTMLElement, actions: { enabled: () => boolean; undo: () => void; redo: () => void }) {
  const keydown = (raw: Event) => {
    const event = raw as KeyboardEvent;
    if (event.defaultPrevented || !actions.enabled() || matchesClosest(event.target, textEntrySelector)) return;
    const command = historyCommand(event, actions); if (!command) return;
    event.preventDefault(); event.stopPropagation();
    if (!event.repeat) command();
  };
  region.addEventListener('keydown', keydown);
  return () => region.removeEventListener('keydown', keydown);
}

export type PathEditorShortcutActions = {
  enabled: () => boolean;
  hasSelection: () => boolean;
  setTool: (tool: 'select' | 'erase') => void;
  selectLinked: () => void;
};

/** Tool shortcuts follow focus in the Path Editor, including toolbar/tab
 * buttons. L expands explicit anchor selection to its connected contours. */
export function bindPathEditorShortcuts(region: HTMLElement, actions: PathEditorShortcutActions) {
  const keydown = (raw: Event) => {
    const event = raw as KeyboardEvent;
    if (event.defaultPrevented || !actions.enabled() || matchesClosest(event.target, textEntrySelector) || event.altKey || event.ctrlKey || event.metaKey) return;
    const key = event.key.toLowerCase();
    if (key !== 'a' && key !== 'e' && (key !== 'l' || !actions.hasSelection())) return;
    event.preventDefault(); event.stopPropagation();
    if (event.repeat) return;
    if (key === 'l') actions.selectLinked();
    else actions.setTool(key === 'a' ? 'select' : 'erase');
  };
  region.addEventListener('keydown', keydown);
  return () => region.removeEventListener('keydown', keydown);
}

/** Observe blank clicks without taking ownership of the canvas pan gesture. */
export function bindPathEditorBlankClick(viewport: HTMLElement, actions: {
  enabled: () => boolean;
  isBlankTarget: (target: EventTarget | null) => boolean;
  clear: () => void;
}) {
  let pending: { pointerId: number; x: number; y: number; dragged: boolean } | undefined;
  const modified = (event: PointerEvent) => event.altKey || event.shiftKey || event.ctrlKey || event.metaKey;
  const cancel = () => { pending = undefined; };
  const down = (raw: Event) => {
    const event = raw as PointerEvent;
    cancel();
    if (event.defaultPrevented || !actions.enabled() || !event.isPrimary || event.button !== 0 || modified(event) || !actions.isBlankTarget(event.target)) return;
    pending = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, dragged: false };
  };
  const move = (raw: Event) => {
    const event = raw as PointerEvent;
    if (!pending || event.pointerId !== pending.pointerId) return;
    if (event.defaultPrevented || modified(event)) { cancel(); return; }
    if (Math.hypot(event.clientX - pending.x, event.clientY - pending.y) > 4) pending.dragged = true;
  };
  const up = (raw: Event) => {
    const event = raw as PointerEvent, click = pending;
    if (!click || event.pointerId !== click.pointerId) return;
    cancel();
    if (event.defaultPrevented || !actions.enabled() || !event.isPrimary || event.button !== 0 || modified(event) || click.dragged || Math.hypot(event.clientX - click.x, event.clientY - click.y) > 4) return;
    actions.clear();
  };
  const cancelled = (raw: Event) => {
    if ((raw as PointerEvent).pointerId === pending?.pointerId) cancel();
  };
  viewport.addEventListener('pointerdown', down, true);
  viewport.addEventListener('pointermove', move, true);
  viewport.addEventListener('pointerup', up, true);
  viewport.addEventListener('pointercancel', cancelled, true);
  viewport.addEventListener('lostpointercapture', cancelled, true);
  const blurTarget = viewport.ownerDocument?.defaultView;
  blurTarget?.addEventListener('blur', cancel);
  return () => {
    cancel();
    viewport.removeEventListener('pointerdown', down, true);
    viewport.removeEventListener('pointermove', move, true);
    viewport.removeEventListener('pointerup', up, true);
    viewport.removeEventListener('pointercancel', cancelled, true);
    viewport.removeEventListener('lostpointercapture', cancelled, true);
    blurTarget?.removeEventListener('blur', cancel);
  };
}

export function bindPathEditorKeyboard(element: HTMLElement, actions: PathEditorKeyboardActions, suppliedClock?: EditorAnimationClock) {
  const clock = suppliedClock ?? {
    request: (callback: (time: number) => void) => window.requestAnimationFrame(callback),
    cancel: (id: number) => window.cancelAnimationFrame(id),
    blurTarget: window, visibilityTarget: document, hidden: () => document.hidden,
  };
  const held = new Set<string>();
  let frame: number | null = null, previous: number | null = null, shift = false, moving = false;
  const stop = () => {
    held.clear(); previous = null;
    if (frame !== null) clock.cancel(frame);
    frame = null;
    if (moving) actions.commit?.();
    moving = false;
  };
  const direction = () => {
    let x = 0, y = 0;
    for (const key of held) { x += arrows[key].x; y += arrows[key].y; }
    // Diagonal movement should not travel faster than horizontal movement.
    const norm = Math.hypot(x, y) || 1;
    return { x: x / norm, y: y / norm };
  };
  const tick = (time: number) => {
    frame = null;
    if (!actions.enabled() || !actions.hasSelection() || !held.size) { stop(); return; }
    if (previous !== null) {
      // A capped delta prevents a background-tab pause from jumping a point.
      const distance = Math.min(32, Math.max(0, time - previous)) * .04 * (shift ? 10 : 1);
      const d = direction();
      if (distance && (d.x || d.y)) actions.move(d.x * distance, d.y * distance);
    }
    previous = time;
    frame = clock.request(tick);
  };
  const keydown = (raw: Event) => {
    const event = raw as KeyboardEvent;
    if (event.defaultPrevented || !actions.enabled() || matchesClosest(event.target, `${textEntrySelector}, button`) || event.altKey) return;
    if (event.key === 'Shift' && moving) { shift = true; return; }
    if (event.ctrlKey || event.metaKey) {
      const command = historyCommand(event, actions);
      if (command) { event.preventDefault(); event.stopPropagation(); if (!event.repeat) { stop(); command(); } }
      return;
    }
    if (arrows[event.key] && actions.hasSelection()) {
      event.preventDefault(); event.stopPropagation(); shift = event.shiftKey;
      if (held.has(event.key) || event.repeat) return;
      if (!moving) { moving = true; actions.begin?.(); }
      held.add(event.key);
      const d = arrows[event.key], distance = .25 * (shift ? 10 : 1);
      actions.move(d.x * distance, d.y * distance);
      if (frame === null) frame = clock.request(tick);
      return;
    }
    if ((event.key === 'Delete' || event.key === 'Backspace' || event.key === '-' || event.key === '_') && actions.hasSelection()) {
      event.preventDefault(); event.stopPropagation();
      if (!event.repeat) { stop(); actions.remove(); }
    } else if (event.key === 'Escape') {
      event.preventDefault(); event.stopPropagation(); stop(); actions.clear();
    }
  };
  const keyup = (raw: Event) => {
    const event = raw as KeyboardEvent;
    if (event.key === 'Shift') { shift = false; return; }
    if (!held.has(event.key)) return;
    event.preventDefault(); event.stopPropagation(); held.delete(event.key); shift = event.shiftKey;
    if (!held.size) stop();
  };
  const visibility = () => { if (clock.hidden?.()) stop(); };
  element.addEventListener('keydown', keydown);
  // Listen on the window as well so releasing outside a blurred canvas cannot
  // leave an animation running. A blur always commits and stops the gesture.
  (clock.blurTarget ?? element).addEventListener('keyup', keyup);
  clock.blurTarget?.addEventListener('blur', stop);
  element.addEventListener('blur', stop);
  clock.visibilityTarget?.addEventListener('visibilitychange', visibility);
  return () => {
    stop();
    element.removeEventListener('keydown', keydown);
    (clock.blurTarget ?? element).removeEventListener('keyup', keyup);
    clock.blurTarget?.removeEventListener('blur', stop);
    element.removeEventListener('blur', stop);
    clock.visibilityTarget?.removeEventListener('visibilitychange', visibility);
  };
}
